<template>
  <nav>
    <v-dialog
      v-model="dialog"
      data-testid="product-group-edit-dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      persistent
    >
      <v-card data-testid="edit-product-group-card">
        <v-form
          ref="form"
          v-model="valid"
          @submit.prevent
        >
          <v-toolbar
            dark
            color="accent"
          >
            <v-btn
              data-testid="close-button"
              icon
              dark
              @click="onDialogClose"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title class="text-h5 font-weight-bold">
              {{ $t('editStoreProductTitle') }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn
              data-testid="submit-button"
              type="submit"
              :disabled="loading"
              color="white"
              class="text-body-1 darktext--text text-none rounded-xxl px-4"
              @click="updateStoreProduct"
            >
              {{ $t('save') }}
            </v-btn>
          </v-toolbar>
          <AlertMessages
            v-if="alertValue"
            :alert-type="alertType" 
            :alert-msg="alertMsg"
            @reset="alertValue = false"
          >
          </AlertMessages>
          <v-card-text class="pt-0 pl-6">
            <div class="py-15 px-md-12">
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelTitle') }}
                  </p>
                  <v-text-field
                    v-model="title"
                    data-testid="title-input"
                    solo
                    hide-details="auto"
                    elevation="6"
                    class="rounded-xxl text-body-2 mb-5"
                    :rules="titleRules"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelDescription') }}
                  </p>
                  <editor-text-area-template  class="editor editor-text-area-template" :content="description" 
                  data-testid="description-input" @updateContent="updateContent" :key="updateEditor"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="3"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelPrice') }}
                  </p>
                  <v-text-field 
                    v-model="formattedPrice" 
                    data-testid="price-input" 
                    solo hide-details="auto" 
                    type="number" 
                    @keypress="preventLetters"
                    class="text-body-2 mb-3 v-text-field--rounded number-field-no-arrows" 
                    :placeholder="$t('productPricePlaceholder')" 
                    :hint="$t('productPriceFreeText')" 
                    persistent-hint
                    :rules="priceRules"
                  >
                    <template v-slot:append>
                      <v-select
                        v-model="selectedCurrency"
                        :items="currencies"
                        hide-details
                        solo
                        item-text="attributes.code"
                        item-value="attributes"
                        return-object
                        class="currency-select"
                        :menu-props="{
                          width: '50px',
                          minWidth: '50px',
                        }"
                        data-testid="currency-select"
                      ></v-select>
                    </template>
                </v-text-field>
                </v-col> 
                <v-col
                  cols="12"
                  md="2"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelTax') }}
                  </p>
                    
                  <v-select
                    v-model="selectedTaxes"
                    class="tax-select"
                    :items="listTaxes" 
                    item-value="id" 
                    item-text="name" 
                    solo
                    hide-details="true"
                    append-icon="mdi-chevron-down"
                    dense
                    data-testid="tax-select" 
                  />
                </v-col>
              </v-row>
              <v-row class="mb-6">
                <v-col cols="12" md="6">
                    <p class="font-weight-bold text-h6 darktext--text">
                      {{ $t('participantInformation') }}
                    </p>
                    <v-card 
                      rounded="xxl" 
                      class="mt-3 pb-4"
                    >
                      <v-card-text class="pb-0">
                        <v-row>
                          <v-col cols="4" offset="4">
                              <span class="text-subtitle-2 font-weight-regular">{{ $t('labelActive') }}</span>
                          </v-col>
                          <v-col cols="4">
                              <span class="text-subtitle-2 font-weight-regular">{{ $t('labelRequired') }}</span>
                          </v-col>
                        </v-row>
                      </v-card-text>
                      <v-card-text class="py-0" v-for="attribute in orderParticipantsRequiredFields" :key="attribute">
                        <v-row align="center" justify="center">
                          <v-col cols="4">
                            <span class="text-subtitle-2 font-weight-bold">{{ $t(`label${attribute}`) }}</span>
                          </v-col>
                          <v-col cols="4">
                            <v-checkbox
                              style="width:26px"
                              v-model="checkboxRequiredFields"
                              color="accent"
                              class="no-colored-options mt-1 selected"
                              :data-testid="attribute + '-active-checkbox'"
                              :disabled="true"
                              hide-details
                            />
                          </v-col>
                          <v-col cols="4">
                            <v-checkbox
                              style="width:26px"
                              v-model="checkboxRequiredFields"
                              color="accent"
                              class="no-colored-options mt-1 selected"
                              :data-testid="attribute + '-required-checkbox'"
                              :disabled="true"
                              hide-details
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                      <v-card-text class="py-0" v-for="attribute in Object.keys(additionalAttributesForParticipants)" :key="attribute">
                        <v-row align="center" justify="center" v-if="Object.keys(additionalAttributesForParticipants).length > 0">
                          <v-col cols="4">
                            <span class="text-subtitle-2 font-weight-bold">{{ $t(attribute) }}</span>
                          </v-col>
                          <v-col cols="4">
                            <v-checkbox
                              style="width:26px"
                              v-model="additionalAttributesForParticipants[attribute].active"
                              color="accent"
                              class="no-colored-options mt-1"
                              :class="{ 'selected' : additionalAttributesForParticipants[attribute].active}"
                              :data-testid="attribute + '-active-checkbox'"
                              hide-details
                              @change="setActive(attribute)"
                              :key="resetCheckbox"
                            />
                          </v-col>
                          <v-col cols="4">
                            <v-checkbox
                              style="width:26px"
                              v-model="additionalAttributesForParticipants[attribute].required"
                              color="accent"
                              class="no-colored-options mt-1"
                              :class="{ 'selected' : additionalAttributesForParticipants[attribute].required}"
                              :data-testid="attribute + '-required-checkbox'"
                              :disabled="!additionalAttributesForParticipants[attribute].active"
                              hide-details
                              :key="resetCheckbox"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="3"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelParticipantsLimit') }}
                  </p>
                  <v-text-field 
                    v-model="participantsLimit" 
                    data-testid="participants-limit-input" 
                    solo hide-details="auto" 
                    type="number" 
                    @keypress="preventLetters"
                    class="text-body-2 mb-3 v-text-field--rounded number-field-no-arrows" 
                    :hint="$t('productParticipantsLimitText')" 
                    persistent-hint
                    :rules="participantsLimitRules"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelProduct') }}
                  </p>
                    <span v-if="productEntityType === 'activities'" class="pa-4 d-inline-block" data-testid="connected-entity">{{ $t('connectedProductActivity') }}: {{ productEntity.title }}</span>
                    <span v-else-if="productEntityType === 'hubs'" class="pa-4 d-inline-block" data-testid="connected-entity">{{ $t('connectedProductHub') }}: {{ productEntity.title }}</span>
                    <span v-else class="pa-4 d-inline-block" data-testid="connected-entity">{{ $t('connectedProductWorkflow') }}: {{ productEntity.title }}</span>

                <v-tooltip bottom v-if="productOrders.length > 0" >
                  <template v-slot:activator="{ on }">
                    <div v-on="on" class="d-inline-block">
                    <v-btn 
                      :ripple="false" 
                      :text="productOrders.length > 0 ? false : true" 
                      class="d-inline-block text-none no-text-transform no-hover accent--text rounded-xxl" 
                      data-testid="change-entity-btn" 
                      :disabled="productOrders.length > 0" 
                      @click="showProductSearch = true"
                    >
                      {{ $t('changeProduct') }}
                    </v-btn>
                    </div>
                  </template>
                  <span>{{$t('changeProductTooltip')}}</span>
                </v-tooltip>
                <div v-else class="d-inline-block">
                    <v-btn 
                      :ripple="false" 
                      class="d-inline-block text-none no-text-transform no-hover accent--text rounded-xxl" 
                      data-testid="change-entity-btn" 
                      @click="showProductSearch = true"
                    >
                      {{ $t('changeProduct') }}
                    </v-btn>
                </div>
                <div v-if="productEntityOwner != null" class="px-4">{{ $t('owner') }}: {{ productEntityOwner }}</div>
                <v-autocomplete
                  v-if="showProductSearch"
                  v-model="selectedProduct" 
                  :menu-props="{
                    closeOnContentClick: true,
                    rounded: 'xxl',
                    zIndex: '9999 !important',
                  }" 
                  filled 
                  rounded 
                  deletable-chips 
                  clearable 
                  solo 
                  no-filter 
                  :search-input.sync="searchKeyword" 
                  @click="searchKeyword=''"
                  :items="entries" 
                  :placeholder="$t('productSearchActivityHubWorkflowPlaceholder')" 
                  append-icon="mdi-magnify"
                  background-color="#fff" 
                  data-testid="entity-select" 
                  class="expanding-search text-body-2" 
                  return-object
                >
                    <template v-slot:no-data>
                      <p class="mx-5 mt-4">
                        {{ $t('noDataText') }}
                      </p>
                    </template>
                    <template v-slot:selection="data">
                      <span class="pl-2">{{ data.item.attributes.title }}</span>
                    </template>
                    <template v-slot:item="data" data-testid="suggested-item">
                        <template v-if="(typeof data.item) !== 'object'">
                            <v-list-item-content v-text="data.item" />
                        </template>
                        <template v-else>
                            <v-row no-gutters align="center">
                                <v-col cols="2">
                                  <v-img
                                      v-if="data.item.type == 'activities'"
                                      :src="(data.item.attributes.image) ? data.item.attributes.image : require('../../assets/cover-activity.jpg')"
                                      class="ma-0 mx-2 rounded-lg activity" width="50" height="40"
                                  />
                                  <v-img
                                      v-if="data.item.type == 'hubs'"
                                      :src="(data.item.attributes.image) ? data.item.attributes.image : require('../../assets/background-hub.jpg')"
                                      class="ma-0 mx-2 rounded-lg hub" width="50" height="40"
                                  />
                                </v-col>
                                <v-col cols="10">
                                <v-list-item-content>
                                    <v-list-item-title class="text-no-wrap text-truncate px-2" style="width:280px">
                                      <v-chip v-if="data.item.type == 'hubs'" small color="accent">
                                          <span class="text-uppercase">{{ $t('hub') }}</span>
                                      </v-chip>
                                      <v-chip v-if="data.item.type == 'workflows'" small color="accent">
                                          <v-icon small>mdi-sitemap</v-icon>
                                      </v-chip>
                                      {{ data.item.attributes.title }}
                                    </v-list-item-title>
                                </v-list-item-content>
                              </v-col>
                            </v-row>
                        </template>
                    </template>
                    <template v-slot:prepend-inner>
                      <v-progress-circular
                        v-if="loadingEntities"
                        size="20"
                        indeterminate
                      />
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelCoverImage') }}
                  </p>
                  <v-hover>
                    <template v-slot:default="{ hover }">
                      <v-card 
                        data-testid="product-cover-card" 
                        rounded="md" 
                        class="mx-auto pa-0 rounded-xxl"
                        :elevation="hover ? 8 : 5" 
                        max-width="600"
                      >
                        <v-card-actions class="pa-0 ">
                          <v-img class="rounded-xxl" max-height="280" :height="cover == null ? '280' : 'auto'" :src="cover" data-testid="cover-image"/>
                          <v-file-input ref="file_input_cover" v-model="coverImageData" accept="image/png, image/jpeg"
                            data-testid="cover-file-input" class="d-block product-cover-image" @change="uploadCover"/>
                          <div class="center-button-on-image">
                            <v-btn :loading="loadingCover" data-testid="upload-cover-button" mx-auto color="accent"
                              class="rounded-xxl text-none px-10" elevation="2" relative bottom left @click="showUploadCover">
                              {{ $t('upload') }}
                            </v-btn>
                            <v-btn data-testid="upload-unsplash-cover-button" mx-auto color="accent"
                              class="rounded-xxl text-none px-10 d-block mx-auto mt-3" :class="{'disable-events': loadingCover}" elevation="2" relative bottom left @click="toggleUnsplash">
                              {{ $t('browse') }}
                            </v-btn>
                          </div>
                        </v-card-actions>
                      </v-card>
                    </template>
                  </v-hover>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>
    
    <UnsplashSearch 
      v-if="showUnsplash"
      ref="unsplashDialog"
      :visible="showUnsplash"
      data-testid="unsplash-dialog"
      @close="showUnsplash = false"
      @applyPhoto="embedUnsplash"
    />
  </nav>
</template>
<script>
import AlertMessages  from '../../components/AlertMessages'
import { ruleRequired, ruleValidMin2Max70Characters, priceRuleValidMax, limitParticipantsRuleValidMax } from '../../rules/formRules.js';
import { getCurrencies, uploadCoverImage, updateProduct, getAvailableAdditionalOrderParticipantAttributes } from '../../api/productApi'
import i18n from '../../plugins/i18n'
import UnsplashSearch from '@/components/unsplash/UnsplashSearch.vue';
import EditorTextAreaTemplate from '../../components/EditorTextAreaTemplate';
import { searchActivitiesIsBooster } from '../../api/activityApi'
import { searchHubsIsBooster } from '../../api/hubApi'
import { searchWorkflowsIsBooster } from '../../api/workflowApi'

import _ from 'lodash'

export default {
  components: {
    UnsplashSearch,
    EditorTextAreaTemplate,
    AlertMessages
  },
  props: {
        visible: Boolean,
        productTitle: {
          type: String,
          default: ''
        },
        productDescription: {
          type: String,
          default: ''
        },
        productPrice: {
          type: Number,
          default: null
        },
        productCurrency: {
          type: Number,
          default: 1
        },
        productTax: {
          default: ''
        },
        productEntity: {
          type: Object,
          default: () => {}
        },
        productEntityType: {
          type: String,
          default: ''
        },
        productEntityOwner: {
          default: null
        },
        productImageURL: {
          type: String,
          default: ''
        },
        productImagePath: {
          type: String,
          default: ''
        },
        productId: {
          type: String,
          default: ''
        },
        productOrders: {
          type: Array,
          default: () => []
        },
        productParticipantsLimit: {
          default: null
        },
        productAdditionalAttributesForParticipants: {
          type: Object,
          default: () => {}
        }
    },
  data(){
        return{
            valid: true,
            alertMsg: '',
            alertType:'',
            alertValue:false,
            loading: false,
            val: {},
            valTitle: '',
            valDescription: '',
            valPrice: 0,
            valCurrency: {id:1, attributes: {name: 'Swedish Krona', code: 'SEK', symbol: 'kr'}},
            valCover: '',
            valCoverPath: '',
            valParticipantsLimit: null,
            titleRules: ruleRequired('labelTitle').concat(ruleValidMin2Max70Characters('labelTitle')),
            // descriptionRules: descriptionRuleValidMin.concat(descriptionRuleValidMax),
            currencies: [],
            selectedCurrency: {},
            searchKeyword: null,
            entries: [],
            spaceId: window.$cookies.get('space_id'),
            userId: window.$cookies.get('id'),
            coverImageData: null,
            coverURL: null,
            loadingCover: false,
            showUnsplash: false,
            selectedProduct: {},
            selectedProductOrg: {},
            loadingEntities: false,
            showProductSearch: false,
            priceRules: priceRuleValidMax,
            participantsLimitRules: limitParticipantsRuleValidMax,
            selectedTaxes: {},
            listTaxes: [
                {
                    id: 0,
                    name: '0%'
                },
                {
                    id: 6,
                    name: '6%'
                },
                {
                    id: 12,
                    name: '12%'
                },
                {
                    id: 25,
                    name: '25%'
                }
            ],
            availableAdditionalOrderParticipantAttributes: {},
            valAdditionalAttributesForParticipants: {},
            resetCheckbox: null,
            updateEditor: null,
            checkboxRequiredFields: true,
            orderParticipantsRequiredFields: ['FirstName', 'LastName', 'Email'],
         }
    },
    computed: {
      productPlan() {
        return this.$store.state.productPlan;
      },
      dialog: {
        get () {
          return this.visible
        },
        set (value) {
          if (!value) {
            this.$emit('close')
          }
          
        }
      },
      title: {
        get()
        {
          return this.valTitle
        },
        set(productTitle)
        {
          this.valTitle = productTitle
        }
      },
      description: {
        get()
        { 
          return this.valDescription
        },
        set(productDescription)
        {
          this.valDescription = productDescription
        }
      },
      price: {
        get()
        {
          return this.valPrice
        },
        set(productPrice)
        {
          this.valPrice = productPrice
        }
      },
      currency: {
        get()
        {
          return this.valCurrency
        },
        set(productCurrency)
        {
          this.valCurrency = productCurrency
        }
      },
       tax: {
        get()
        {
          return this.valTax
        },
        set(productTax)
        {
          this.valTax = productTax
        }
      },
      cover:
      {
          get()
          {
              return this.valCover
          },
          set(productImageURL)
          {
              this.valCover = productImageURL
          }
      },
      coverPath:
      {
          get()
          {
              return this.valCoverPath
          },
          set(productImagePath)
          {
              this.valCoverPath = productImagePath
          }
      },
      participantsLimit: {
        get()
        {
          return this.valParticipantsLimit
        },
        set(productParticipantsLimit)
        {
          this.valParticipantsLimit = productParticipantsLimit
        }
      },
      additionalAttributesForParticipants: {
        get()
        {
          return this.valAdditionalAttributesForParticipants
        },
        set(productAdditionalAttributesForParticipants)
        {
          this.valAdditionalAttributesForParticipants = productAdditionalAttributesForParticipants
        }
      },
      formattedPrice: {
        get() {
          return this.price.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true, style: 'decimal' }).replace(',', '.');
        },
        set(value) {
          this.price = parseFloat(value.replace('.', ''));
        },
      },
    },
    watch :{
      productTitle(newVal) {
          this.valTitle = newVal
      },
      productDescription(newVal) {
          this.valDescription = newVal
      },
      productPrice(newVal) {
          this.valPrice = newVal
      },
      productCurrency(newVal) {
          this.valCurrency = newVal
      },
      productTax(newVal) {
          this.valTax = newVal
      },
      productImageURL(newValCover){
          this.valCover = newValCover
      },
      productImagePath(newValCoverPath){
          this.valCoverPath = newValCoverPath
      },
      productParticipantsLimit(newVal) {
          this.valParticipantsLimit = newVal
      },
      productAdditionalAttributesForParticipants(newVal) {
          this.valAdditionalAttributesForParticipants = newVal
      },
      searchKeyword(val) {
        if (val == null || val == '') {
          this.entries = []
          return
        }
        let searchkey = val.trim()
        if (searchkey.length < 2) return
        this.searchActivitiesHubsAndWorkflows(searchkey)
      },
      selectedProduct(val) {
        if(val == null){
          this.selectedProduct = {
            id: this.productEntity.uuid,
            type: this.productEntityType,
            attributes:this.productEntity
          }
        }
      }
    },
    async created(){
      await this.fetchCurrencies();
       this.updateEditor = Date.now()
      this.title = this.productTitle
      this.description =this.productDescription
      this.price = this.productPrice
      this.currency = this.productCurrency
      this.tax = this.productTax
      this.cover = this.productImageURL
      this.coverPath = this.productImagePath
      this.selectedProduct = {
        id: this.productEntity.uuid,
        type: this.productEntityType,
        attributes:this.productEntity
      }
      this.selectedProductOrg = {
        id: this.productEntity.uuid,
        type: this.productEntityType,
        attributes:this.productEntity
      }
      this.selectedCurrency = this.currencies.filter(item => item.id == this.currency)[0]
      this.selectedTaxes = this.listTaxes.filter(item => item.id == this.tax)[0].id
      this.participantsLimit = this.productParticipantsLimit
      this.additionalAttributesForParticipants = this.productAdditionalAttributesForParticipants
      await this.fetchAvailableAdditionalOrderParticipantAttributes();
      
    },
    methods:{
      async updateStoreProduct(){
          this.$refs.form.validate()
          if(this.selectedProduct != null && this.valid) {
            var alertType = ''
            var alertMsg = ''
            var attributes = {}
            var relationships = {}
            let additionalAttributes = JSON.parse(JSON.stringify(this.additionalAttributesForParticipants))
            let additionalAttributesFinal = {}

            Object.keys(additionalAttributes).forEach(function(key) {
                if (additionalAttributes[key].active) {
                  additionalAttributesFinal[key] = {'required': additionalAttributes[key].required}   
                }
            });

            additionalAttributesFinal = JSON.parse((JSON.stringify(additionalAttributesFinal)))

            
            if(this.coverPath == null){
              attributes = {
                title: this.title.trim(),
                description:this.description == null || this.description == '<p></p>' ? null : this.description,
                price: this.price == null || this.price == '' ? 0 : parseInt(this.price),
                tax: parseInt(this.selectedTaxes),
                currency_id: parseInt(this.selectedCurrency.id),
                participants_limit: this.participantsLimit === null ? null : parseInt(this.participantsLimit),
                additional_attributes_for_participants: additionalAttributesFinal,
              }
            }else{
                attributes= {
                  title: this.title.trim(),
                  image: this.coverPath,
                  description:this.description == null || this.description == '<p></p>' ? null : this.description,
                  price: this.price == null || this.price == '' ? 0 : parseInt(this.price),
                  tax: parseInt(this.selectedTaxes),
                  currency_id: parseInt(this.selectedCurrency.id),
                  participants_limit: this.participantsLimit === null ? null : parseInt(this.participantsLimit),
                  additional_attributes_for_participants: additionalAttributesFinal,
              }
            }
            if(this.selectedProduct.id == this.selectedProductOrg.id){
              relationships = {
                space: {
                  data: {
                    type: "spaces",
                    id: "" + this.spaceId + ""
                  },
                }
              }
            }else {
              relationships = {
                space: {
                  data: {
                    type: "spaces",
                    id: "" + this.spaceId + ""
                  },
                },
                productable: {
                  data: {
                    type: this.selectedProduct.type,
                    id: this.selectedProduct.id
                  }
                }
              }
            }
            try {
              let data = {
                data: {
                  type: "store-products",
                  id: this.productId,
                  attributes: attributes,
                  relationships: relationships
                }
              };
              var response = await updateProduct(this.productId, data);
              if (response) {
                if (response.status == 200) {
                  // this.updateEditor = Date.now()
                  alertMsg =  i18n.t('editProductSuccess')
                  alertType = 'success'
                  this.$emit('proceedSubmit', alertMsg, alertType)
                  this.dialog = false   
                }
              }
            } catch (e) {
              if (e.response) {
                if (e.response.status == 403) {
                  this.$router.push({ name: 'PageAccessDenied' })
                } else {
                  alertType = 'error'
                  alertMsg =  i18n.t('serverError')
                  this.$emit('proceedSubmit', alertMsg, alertType)
                  this.$emit('close') 
                }
              } else {
                alertType = 'error'
                alertMsg =  i18n.t('serverError')
                this.$emit('proceedSubmit', alertMsg, alertType)
                this.$emit('close')
              }
            }
          }
      },
      async fetchAvailableAdditionalOrderParticipantAttributes(){
          try {
              const response = await getAvailableAdditionalOrderParticipantAttributes();
              let availableAttributesReady = {}
              if(response.status == 200) {
                  this.availableAdditionalOrderParticipantAttributes = response.data.meta.additional_order_participant_attributes;
                  this.availableAdditionalOrderParticipantAttributes.forEach(attribute => {                
                    if (Object.prototype.hasOwnProperty.call(this.additionalAttributesForParticipants, attribute)) {
                      availableAttributesReady[attribute] = {active: true, required: this.additionalAttributesForParticipants[attribute].required}
                    } else {
                      availableAttributesReady[attribute] = {active: false, required: false}
                    }
                  })
              }    
              this.additionalAttributesForParticipants = availableAttributesReady
              this.resetCheckbox = Date.now()   
          } catch (e) {
              if (e.response) {
                  this.alertValue = true;
                  this.alertType = "error";
                  this.alertMsg = i18n.t("serverError");
              }
          }
      },
      setActive(attribute) {
        this.additionalAttributesForParticipants[attribute].required = false
        this.resetCheckbox = Date.now()
      }, 
      onDialogClose(){
        this.$emit('close')       
      },
      preventLetters(e) {
        e = e || window.event;
        var charCode = (typeof e.which == "undefined") ? e.keyCode : e.which;
        var charStr = String.fromCharCode(charCode);

        if (!charStr.match(/^[0-9]+$/))
          e.preventDefault();

        if (e.target.value >= 999999999)
          e.preventDefault();
      },
      async fetchCurrencies(){
        try {
            const response = await getCurrencies();
            if(response.status == 200) {
                this.currencies = response.data.data;
            }                
        } catch (e) {
            if (e.response) {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("serverError");
            }
        }
      },
      searchActivitiesHubsAndWorkflows:_.debounce(async function(keyword) {
        this.loadingEntities = true
        var searchActivities = []
        var searchHubs = []
        var searchWorkflows = []
        if (keyword && keyword.trim().length >= 2) {
          try {
            const response = await searchActivitiesIsBooster(this.spaceId, keyword)
            if (response) {
              if (response.status == 200) {
                searchActivities = response.data.data
              }
            }
          } catch (e) {
            if (e.response) {
              this.alertType = 'error'
              this.alertMsg = i18n.t('serverError')
              this.alertValue = true
            }
          }
          if (this.productPlan.attributes && this.productPlan.attributes.name != 'Free') {
            try {
              const response = await searchHubsIsBooster(this.spaceId, keyword)
              if (response) {
                if (response.status == 200) {
                  searchHubs = response.data.data
                }
              }
            } catch (e) {
              if (e.response) {
                this.alertType = 'error'
                this.alertMsg = i18n.t('serverError')
                this.alertValue = true
              }
            }
            if(this.productPlan.attributes.workflow_module == 1){
              try {
                const response = await searchWorkflowsIsBooster(this.spaceId, keyword)
                if (response) {
                  if (response.status == 200) {
                    searchWorkflows = response.data.data
                  }
                }
              } catch (e) {
                if (e.response) {
                  this.alertType = 'error'
                  this.alertMsg = i18n.t('serverError')
                  this.alertValue = true
                }
              }
            }
          }
          Array.prototype.push.apply(searchActivities, searchHubs)
          Array.prototype.push.apply(searchActivities, searchWorkflows)
          searchActivities.sort(function (a, b) { return (a.sort_term > b.sort_term) ? 1 : ((b.sort_term > a.sort_term) ? -1 : 0) })
          this.entries = searchActivities
          this.loadingEntities = false
        }
      }, 500),
    async uploadCover(){
            if (this.coverImageData.size > 5*1024*1024){
                this.alertType = 'error'
                this.alertMsg =  i18n.t('uploadCoverError')
                this.alertValue = true
            } else {
                this.loadingCover = true
                try {
                    let data = new FormData();
                    data.append('file', this.coverImageData);
                    data.append('type', 'cover');
                    data.append('space_id', this.spaceId);

                    const response = await uploadCoverImage(data) 
                    if(response.status == 200) {
                        this.loadingCover = false
                        this.alertMsg = ''
                        this.cover = URL.createObjectURL(this.coverImageData)
                        this.coverPath = response.data.meta.detail
                    }
                } catch (e) {
                    if (e.response){
                        if (e.response.status == 422 ){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize'){
                                    this.alertValue = true
                                    this.alertType = 'error'
                                    this.alertMsg =  i18n.t('uploadCoverError')
                                }else {
                                    this.alertValue = true
                                    this.alertType = 'error'
                                    this.alertMsg = i18n.t('serverError')
                                }
                            }
                        } else if (e.response.status == 403){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'Storage is full'){
                                    this.alertValue = true
                                    this.alertType = 'error'
                                    this.alertMsg =  i18n.t('storageFullError')
                                }
                                else {
                                    this.$router.push({name: 'PageAccessDenied'})
                                }
                            }
                        } else {
                            this.alertType = 'error'
                            this.alertValue = true
                            this.alertMsg =  i18n.t('serverError')       
                        }                     
                    }
                    this.loadingCover = false
                }
            }
        },
        updateContent(content) {
          if (content != '') {
            this.description = content
          }
        },
        toggleUnsplash(){
          this.showUnsplash = !this.showUnsplash
        },

        embedUnsplash(photo){
          this.showUnsplash = false
          this.cover = photo.urls.regular
          this.coverPath = photo.urls.regular
        },
        showUploadCover() {
          this.$refs.file_input_cover.$refs.input.click()
        },
      }
}
</script>